/**
 * Lot Polygon Colors DOM Component
 *
 * Sole owner of color-related class mutations on the ISM SVG overlay.
 * Applies base color classes, selected state, hover state, and chip-filter matched/unmatched
 * state to every element sharing a given lot id — both the LotPolygons shape and the LotNumbers
 * label (see findLotElements below). Does not know about Redux or business logic — receives the
 * lot status map (and, for filtering, the matched lot id set) from the plugin.
 *
 * @module nativeInteractiveSiteMap/components/lotPolygonColors.dom
 */

import { LOT_INTERACTIVE_LAYERS_SELECTOR } from '../handlers/lotPolygonInteractionHandlers';

const LOT_CLASS_BASE = 'nhs-c-native-interactive-site-map__lot';
const LOT_SELECTED_CLASS = `${LOT_CLASS_BASE}--selected`;
const LOT_HOVERED_CLASS = `${LOT_CLASS_BASE}--hovered`;
const LOT_UNMATCHED_CLASS = `${LOT_CLASS_BASE}--unmatched`;

/**
 * A lot id is not unique in the DOM: the LotPolygons layer (the shape) and the LotNumbers layer
 * (the label) each carry their own element for the same lot, both stamped with the same id — see
 * LOT_INTERACTIVE_LAYERS_SELECTOR (lotPolygonInteractionHandlers.js), which treats both layers as
 * valid lot hit targets for click/hover. svgElement.getElementById(lotId) would only ever return
 * one of them, silently leaving the other with none of these classes (and, for --unmatched's
 * pointer-events: none, still clickable/hoverable) — so every mutation here targets every element
 * sharing the id, not just the first one found.
 *
 * Scoped to LOT_INTERACTIVE_LAYERS_SELECTOR — the same boundary svgLotInteraction.js's own
 * findLotElement uses via .closest() — so an id collision with an unrelated layer (Buildings,
 * Amenity, LotGroup, etc. can carry ids too, per lotPolygonInteractionHandlers.js) never mutates
 * a non-lot element. lotId is CSS.escape'd since it's interpolated into an attribute selector.
 *
 * @param {SVGSVGElement} svgElement
 * @param {string} lotId
 * @returns {Element[]}
 */
function findLotElements(svgElement, lotId) {
  return Array.from(svgElement.querySelectorAll(`[id="${CSS.escape(lotId)}"]`))
    .filter((element) => element.closest(LOT_INTERACTIVE_LAYERS_SELECTOR));
}

/**
 * Creates a controller for color-related CSS class mutations on lot <path> elements
 * within the given SVG element.
 *
 * @param {SVGSVGElement} svgElement - The root SVG element containing the lot elements
 * @returns {{ applyFilter: Function, setSelected: Function,
 *   setHoveredMany: Function, clearHovered: Function }}
 */
function createLotPolygonColors(svgElement) {
  const state = {
    currentHoveredLotIds: new Set(),
  };

  return {
    /**
     * Applies the status class to every lot element, and toggles the unmatched (grey) class
     * based on whether its id is present in matchedLotIds. NotFound lots never appear in
     * matchedLotIds (NotFound never equals a chip key), so they always end up unmatched too —
     * one class, one CSS rule (pointer-events: none), covers "grey and therefore not
     * clickable/hoverable" for both reasons a lot ends up grey, with no separate JS-side guard.
     * Status never changes after the site map loads, so re-adding it here on every call is a
     * harmless no-op (classList.add is idempotent) — the same function covers both the initial
     * paint and every later chip change, instead of keeping a separate one-time "initial colors"
     * entry point in sync with this one.
     *
     * @param {Record<string, string>} lotStatusMap - { lotId → status string }
     * @param {Set<string>} matchedLotIds - lot ids that match the currently active chip
     */
    applyFilter(lotStatusMap, matchedLotIds) {
      Object.entries(lotStatusMap).forEach(([lotId, status]) => {
        findLotElements(svgElement, lotId).forEach((element) => {
          element.classList.add(`${LOT_CLASS_BASE}--${status}`);
          element.classList.toggle(LOT_UNMATCHED_CLASS, !matchedLotIds.has(lotId));
          element.setAttribute('data-qa', `native_interactive_site_map_lot_${status}`);
        });
      });
    },

    /**
     * Moves the selected class from prevLotId to newLotId.
     * Passing null for either argument is safe.
     *
     * @param {string|null} prevLotId
     * @param {string|null} newLotId
     */
    setSelected(prevLotId, newLotId) {
      if (prevLotId) {
        findLotElements(svgElement, prevLotId).forEach((element) => element.classList.remove(LOT_SELECTED_CLASS));
      }
      if (newLotId) {
        findLotElements(svgElement, newLotId).forEach((element) => element.classList.add(LOT_SELECTED_CLASS));
      }
    },

    /**
     * Moves the hovered class from the previously-hovered set of lot ids to the new set. Diffs
     * against the previous call instead of assuming a single id, so a hover source that maps to
     * several lots at once (e.g. a Floor Plan card — see plugins/cardLotHighlight.plugin.js) and
     * one that only ever maps to a single lot (the native SVG hover) can share this same method.
     *
     * Only ever touches LOT_HOVERED_CLASS. LOT_SELECTED_CLASS has its own independent lifecycle
     * via setSelected and is never read or written here — this is what makes hovering a lot that
     * is also the currently-selected lot safe for free: both classes resolve to the same fill per
     * status in SCSS, so losing LOT_HOVERED_CLASS on hover-out leaves LOT_SELECTED_CLASS (and
     * therefore the correct color) intact.
     *
     * @param {Iterable<string>} lotIds
     */
    setHoveredMany(lotIds) {
      const newHoveredLotIds = new Set(lotIds);

      state.currentHoveredLotIds.forEach((lotId) => {
        if (!newHoveredLotIds.has(lotId)) {
          findLotElements(svgElement, lotId).forEach((element) => element.classList.remove(LOT_HOVERED_CLASS));
        }
      });

      newHoveredLotIds.forEach((lotId) => {
        findLotElements(svgElement, lotId).forEach((element) => element.classList.add(LOT_HOVERED_CLASS));
      });

      state.currentHoveredLotIds = newHoveredLotIds;
    },

    /**
     * Removes the hovered class from every currently-hovered lot.
     * No-op if nothing is currently hovered.
     */
    clearHovered() {
      state.currentHoveredLotIds.forEach((lotId) => {
        findLotElements(svgElement, lotId).forEach((element) => element.classList.remove(LOT_HOVERED_CLASS));
      });
      state.currentHoveredLotIds = new Set();
    },
  };
}

export default createLotPolygonColors;
